import "../../App.css";

import { zodResolver } from "@hookform/resolvers/zod";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  Menu,
  Modal,
  Select,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import type { ButtonPropsColorOverrides } from "@mui/material/Button/Button";
import CircularProgress from "@mui/material/CircularProgress";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import type { OverridableStringUnion } from "@mui/types";
import { AxiosError } from "axios";
import head from "lodash/head";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";

import {
  CLAIMS_SORT_FIELD_MAP,
  mapClaimFilterField,
  mapClaimSortField,
  normalizePayer,
} from "../../api/filters/claimFilters";
import useQueryClient from "../../api/query";
import { toSourceUrls } from "../../api/sources";
import { formatMoney } from "../../formatters";
import {
  ClaimStatus,
  DenialStatus,
  PREDEFINED_CLAIM_FILTERS,
  type REVIEW_VIEW,
  ReviewStatus,
  WieldyStatus,
} from "../../status";
import {
  type BackendFilters,
  createDefaultBackendFilters,
} from "../../utils/backendFilters";
import { useConfig } from "../../utils/config";
import useDevMode from "../../utils/devmode";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "../../utils/featureFlags";
import {
  enumToStrings,
  isoNow,
  isoToDate,
  snakeToTitle,
  sortAlphabetically,
  sortColumnsByFieldOrder,
  toPascalCase,
  toValueOptions,
} from "../../utils/utils";
import actionLabels, { Action, needsWorkActionLabels } from "../actions";
import { BankMatchState } from "../bankMatch";
import DataTable, {
  type CellParams,
  type ColumnVisibilityModel,
  type DataTableColDef,
  EMPTY_FILTER_OPERATORS,
  FILTER_OPERATORS,
  loadTabVisibility,
  type PaginationModel,
  type SortModel,
  saveTabVisibility,
  useServerFilterModel,
} from "../DataTable";
import DownloadSource, { toPDFFileName } from "../PDFViewer/DownloadSource";
import {
  canMarkPosted,
  hasFailed,
  hasPosted,
  PostedState,
  partialPosting,
  unpostBlocked,
} from "../posting";
import RequeueHint from "../RequeueHint";
import { redactText, renderCellRedacted } from "../redact";
import CustomNoRowsOverlay from "../TableNoDataOverlay";
import UnpostDialog from "../UnpostDialog";
import urgencySelectColor, { Urgency } from "../urgencyColors";
import AddEntityActions from "./AddEntityActions";
import ClaimDetails from "./ClaimDetails";
import ClaimForm from "./ClaimForm";
import {
  DEFAULT_COLUMN_ORDER,
  DETAIL_TOGGLE_FIELD,
  getAllClaimsColumnVisibility,
  getDefaultColumnVisibility,
  getDenialsColumnVisibility,
  getReviewColumnVisibility,
} from "./ClaimsTableColumns";
import {
  type AddClaimFormFields,
  claimFormSchemaBase,
  defaultClaimFields,
  editableClaimFields,
} from "./CrudForms.zod";
import SetNextActionDialog from "./SetNextActionDialog";
import { NavigationCell } from "./ui/NavigationCell";

type ClaimRow = ClaimWithProcedureAndPatientMessage;
type ClaimCellParams = CellParams<ClaimRow>;
type ClaimColDef = DataTableColDef<ClaimRow>;

const CLAIM_SELECT_FILTER_OPERATORS = [
  ...FILTER_OPERATORS.singleSelect,
  ...EMPTY_FILTER_OPERATORS,
];

const mapFilterToTab = (filter: PREDEFINED_CLAIM_FILTERS): string => {
  switch (filter) {
    case PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS:
      return "ALL_CLAIMS";
    case PREDEFINED_CLAIM_FILTERS.REVIEW:
      return "REVIEW";
    case PREDEFINED_CLAIM_FILTERS.DENIALS:
      return "DENIALS";
    default:
      return "ALL_CLAIMS";
  }
};

const postingClaimErrors = (
  claim: ClaimWithProcedureAndPatientMessage,
): string[] =>
  [
    {
      value: claim.wieldyPatientName || head(claim.Patient)?.name,
      prettyName: "Patient Name",
    },
    {
      value: claim.patientDob || head(claim.Patient)?.dob,
      prettyName: "Patient Date of Birth",
    },
  ]
    .filter((field) => !field.value)
    .map((field) => field.prettyName);

const postOptionButton = (
  buttonText: React.ReactNode,
  buttonColor: OverridableStringUnion<
    | "inherit"
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "info"
    | "warning",
    ButtonPropsColorOverrides
  >,
  openMenuOptions: boolean,
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void,
  handleClose: () => void,
  anchorEl: HTMLElement | null,
  isPosting: boolean,
  manuallyPost: (() => void) | null,
  post: (() => void) | null,
  toolTipOverride?: React.ReactNode,
) => (
  <>
    <Tooltip
      title={toolTipOverride || "Post to PMS"}
      TransitionComponent={Zoom}
      placement="top"
      arrow
    >
      <Button
        data-testid="postButton"
        disabled={isPosting}
        color={buttonColor}
        variant="contained"
        onClick={handleOpen}
        endIcon={<ArrowDropDownIcon />}
        style={{
          width: "75%",
          color: buttonText !== "Pending" && isPosting ? "#2764EB" : undefined,
        }}
      >
        {buttonText}
      </Button>
    </Tooltip>
    <Menu
      id="pms-menu"
      aria-labelledby="pms-menu"
      anchorEl={anchorEl}
      open={openMenuOptions}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      sx={{
        marginTop: "1rem",
      }}
    >
      {post ? (
        <MenuItem data-testid="autoPost" onClick={post}>
          Auto Post to PMS
        </MenuItem>
      ) : null}
      <MenuItem
        data-testid="manualPost"
        onClick={manuallyPost ?? undefined}
        disabled={!manuallyPost}
      >
        <ListItemText
          primary="Manually Post to PMS"
          secondary={manuallyPost ? null : "Claim must be Accepted or Bypass"}
        />
      </MenuItem>
    </Menu>
  </>
);

const missingInfoButton = (
  errors: string[],
  path: string,
  searchParams: URLSearchParams,
) => (
  <Tooltip
    title={
      <div style={{ whiteSpace: "pre-line" }}>
        {`Missing fields: ${errors.join(
          ", ",
        )}. Please update the claim and try again.`}
      </div>
    }
    TransitionComponent={Zoom}
    placement="top"
    arrow
  >
    <Link to={`${path}?${searchParams}`} style={{ width: "75%" }}>
      <Button
        data-testid="missingPostingButton"
        variant="contained"
        color="warning"
        endIcon={<ErrorOutlineIcon />}
      >
        Missing
      </Button>
    </Link>
  </Tooltip>
);

const BLOCKER_LABELS: Record<string, string> = {
  WIELDY_STATUS_INACTIVE: "Inactive payer source",
  CLAIM_TYPE_NOT_CLAIM: "Not a standard claim type",
  CLAIM_STATUS_NOT_ACTIONABLE: "Claim status not actionable",
  REVIEW_STATUS_NOT_ACCEPTED: "Claim not yet accepted",
  PATIENT_MISSING: "Patient record not found",
  PATIENT_DOB_MISSING: "Patient DOB is missing",
  PAYER_PAYS_NEGATIVE: "Negative payer pays — post manually",
  PRIOR_PAID_ADJUSTMENT_PRESENT: "Prior paid adjustment — post manually",
  PAYMENT_MISSING: "No payment record",
  PAYMENT_TYPE_NOT_POSTABLE: "Check/virtual card — post manually",
  INTEREST_LINE_ITEM_PRESENT: "Interest payment — post manually",
  PENDING_DENIAL: "Pending denial — resolve first",
};

const notReadyButton = (
  claim: ClaimWithProcedureAndPatientMessage,
  manualPost: (() => void) | null,
  openPostingOptions: boolean,
  handleClickPostOptions: (event: React.MouseEvent<HTMLElement>) => void,
  handleClosePostOptions: () => void,
  postingAnchorEl: HTMLElement | null,
) => {
  const blockers = claim.postingBlockers || [];
  const reasons = blockers.map((b) => BLOCKER_LABELS[b]).filter(Boolean);
  const tooltipText = reasons.length
    ? `${reasons.join(". ")}.`
    : claim.postedLatestErrorMessage || "Claim is not ready to post.";

  return postOptionButton(
    "Not Ready",
    "warning",
    openPostingOptions,
    handleClickPostOptions,
    handleClosePostOptions,
    postingAnchorEl,
    false,
    manualPost,
    null,
    tooltipText,
  );
};

const withRemainingSteps = (
  label: string,
  partial: ReturnType<typeof partialPosting>,
  shortLabel: string = label,
) =>
  partial?.missing.length
    ? `${shortLabel} ${partial.missing.length}/${partial.landed.length + partial.missing.length}`
    : label;

const stepsTooltip = (
  partial: ReturnType<typeof partialPosting>,
  message: string | undefined,
) =>
  partial ? (
    <Box data-testid="partialPostingSteps">
      {partial.landed.map((step) => (
        <div key={step}>✓ {step}</div>
      ))}
      {partial.missing.map((step) => (
        <div key={step}>✗ {step}</div>
      ))}
      {message ? <Box sx={{ marginTop: 0.75 }}>{message}</Box> : null}
    </Box>
  ) : (
    message
  );

const postButton = (
  props: ClaimCellParams,
  markManuallyPosted: (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    newIsPosted: boolean,
  ) => void,
  postToPMS: (claimToUpdate: ClaimWithProcedureAndPatientMessage) => void,
  searchParams: URLSearchParams,
) => {
  const theme = useTheme();
  const claim: ClaimWithProcedureAndPatientMessage = props.row;
  const errors = postingClaimErrors(claim);
  const pathName = useLocation().pathname;
  const [postingAnchorEl, setPostingAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const openPostingOptions = Boolean(postingAnchorEl);

  const handleClickPostOptions = (event: React.MouseEvent<HTMLElement>) => {
    setPostingAnchorEl(event.currentTarget);
  };
  const handleClosePostOptions = () => {
    setPostingAnchorEl(null);
  };

  if (
    errors.length &&
    claim.postedState === PostedState[PostedState.UNPOSTED]
  ) {
    const path = claim.wieldyPatientId
      ? `/patients/${claim.wieldyPatientId}`
      : "/patients/new-patient";
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("redirect", pathName);
    return missingInfoButton(errors, path, newSearchParams);
  }

  const manualPost = canMarkPosted(claim)
    ? () => {
        markManuallyPosted(claim, true);
        handleClosePostOptions();
      }
    : null;

  if (
    claim.postedState === PostedState[PostedState.UNPOSTED] &&
    claim.postingBlockers?.length
  ) {
    return notReadyButton(
      claim,
      manualPost,
      openPostingOptions,
      handleClickPostOptions,
      handleClosePostOptions,
      postingAnchorEl,
    );
  }

  const canDocCabPost =
    claim.claimPayerPays !== null &&
    claim.claimPayerPays >= 0 &&
    !!claim.documents?.[0]?.[1]?.length;
  const canLedgerPost =
    claim.claimPayerPays !== null && claim.claimPayerPays > 0;

  if (hasPosted(claim)) {
    return (
      <Button
        data-testid="posted"
        variant="text"
        style={{ width: "75%", color: theme.palette.success.main }}
      >
        {claim.postedState === PostedState[PostedState.MANUALLY_POSTED]
          ? "Manually Posted"
          : "Auto Posted"}
      </Button>
    );
  }

  const partial = partialPosting(claim);

  if (claim.postedState === PostedState[PostedState.FAILURE]) {
    return postOptionButton(
      withRemainingSteps("Failure", partial),
      "error",
      openPostingOptions,
      handleClickPostOptions,
      handleClosePostOptions,
      postingAnchorEl,
      false,
      manualPost,
      null,
      stepsTooltip(
        partial,
        claim.postedLatestErrorMessage
          ? `Failed to Post to PMS. ${claim.postedLatestErrorMessage.replace(/\.$/, "")}. Please post manually.`
          : "Failed to Post to PMS. Please post manually.",
      ),
    );
  }
  if (claim.postedState === PostedState[PostedState.RETRY]) {
    return postOptionButton(
      withRemainingSteps("Retry Post", partial, "Retry"),
      "warning",
      openPostingOptions,
      handleClickPostOptions,
      handleClosePostOptions,
      postingAnchorEl,
      false,
      manualPost,
      canDocCabPost || canLedgerPost
        ? () => {
            postToPMS(claim);
            handleClosePostOptions();
          }
        : null,
      stepsTooltip(partial, claim.postedLatestErrorMessage || undefined),
    );
  }
  if (!canDocCabPost && canLedgerPost) {
    return postOptionButton(
      "Post Ledger",
      "primary",
      openPostingOptions,
      handleClickPostOptions,
      handleClosePostOptions,
      postingAnchorEl,
      false,
      manualPost,
      () => {
        postToPMS(claim);
        handleClosePostOptions();
      },
      "PDF Missing - Manually Post to Doc Cabinet",
    );
  }
  if (
    claim.postedState === PostedState[PostedState.PENDING] ||
    claim.postedState === PostedState[PostedState.REQUEUED]
  ) {
    return (
      <>
        {postOptionButton(
          "Pending",
          "primary",
          openPostingOptions,
          handleClickPostOptions,
          handleClosePostOptions,
          postingAnchorEl,
          true,
          manualPost,
          canDocCabPost || canLedgerPost
            ? () => {
                postToPMS(claim);
                handleClosePostOptions();
              }
            : null,
        )}
        <RequeueHint
          requeue={
            claim.postedState === PostedState[PostedState.REQUEUED]
              ? { reason: claim.postedLatestErrorMessage, at: claim.updatedAt }
              : undefined
          }
        />
      </>
    );
  }
  return postOptionButton(
    [PostedState[PostedState.POSTING]].includes(claim.postedState) ? (
      <>
        Posting
        <CircularProgress size={11} sx={{ ml: 0.5 }} color="inherit" />
      </>
    ) : (
      "Post"
    ),
    "primary",
    openPostingOptions,
    handleClickPostOptions,
    handleClosePostOptions,
    postingAnchorEl,
    claim.postedState === PostedState[PostedState.POSTING],
    manualPost,
    canDocCabPost || canLedgerPost
      ? () => {
          postToPMS(claim);
          handleClosePostOptions();
        }
      : null,
  );
};

function renderUrgency(
  props: ClaimCellParams,
  claimToUpdate: (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void,
) {
  const urgency = Urgency[props.value as keyof typeof Urgency];
  return (
    <FormControl fullWidth>
      <Select
        style={{
          color:
            typeof urgency === "number"
              ? urgencySelectColor.get(urgency)?.color
              : undefined,
          borderRadius: "12px",
        }}
        displayEmpty
        labelId="urgency-select-label"
        id="urgency-simple-select"
        value={urgency ?? "-"}
        onChange={(e) => {
          const urgeToSet = Urgency[e.target.value as keyof typeof Urgency];
          const claim = props.row;
          claimToUpdate(claim, {
            wieldyId: claim.wieldyId,
            practiceId: claim.practiceId,
            urgency: urgeToSet?.toString() ?? null,
          });
        }}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            border: 0,
          },
        }}
      >
        <MenuItem style={{ color: "#475467" }} value="-">
          -
        </MenuItem>
        {Array.from(urgencySelectColor.keys()).map((key) => (
          <MenuItem
            key={key}
            style={{ color: urgencySelectColor.get(key)?.color }}
            value={key}
          >
            {urgencySelectColor.get(key)?.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

function renderAction(
  props: ClaimCellParams,
  claimToUpdate: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void,
) {
  const { value } = props;
  const displayLabel = actionLabels.get(value as Action)?.label ?? "-";

  return (
    <Tooltip title={displayLabel} placement="top" arrow>
      <FormControl fullWidth>
        <Select
          displayEmpty
          labelId="action-select-label"
          id="action-simple-select"
          value={(value as Action) ?? "-"}
          onChange={(e) => {
            const actionToSet = Action[e.target.value as keyof typeof Action];
            const claim = props.row;
            claimToUpdate(claim, {
              wieldyId: claim.wieldyId,
              practiceId: claim.practiceId,
              action: actionToSet ?? null,
            });
          }}
          sx={{
            ".MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
            borderRadius: "12px",
          }}
        >
          <MenuItem value="-">-</MenuItem>
          {Array.from(actionLabels.keys()).map((key) => (
            <MenuItem key={key} value={key}>
              {actionLabels.get(key)?.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
}

function renderActionDenise(
  props: ClaimCellParams,
  pendingActionsByClaimId: Record<string, Action | null>,
  setPendingActionForClaim: (
    claimId: string,
    action: Action | null | undefined,
  ) => void,
  inFlightDenialIds: Set<string>,
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => void,
) {
  const { row } = props;

  if (row.claimStatus !== ClaimStatus.DENIED) {
    return null;
  }

  const hasPendingChange = row.wieldyId in pendingActionsByClaimId;
  const isAutoResolved = row.approvedClaimKey != null;
  const isInFlight = inFlightDenialIds.has(row.wieldyId);
  const isImmediateMode = onDenialUpdate !== undefined;

  // Display: pending change > userAction > suggestedAction > "-"
  let displayValue: string;
  if (!isImmediateMode && hasPendingChange) {
    const pending = pendingActionsByClaimId[row.wieldyId];
    displayValue = pending != null ? pending : "-";
  } else {
    displayValue = row.userAction ?? row.suggestedAction ?? "-";
  }

  const optionLabels = isImmediateMode ? needsWorkActionLabels : actionLabels;
  const displayLabel = optionLabels.get(displayValue as Action)?.label ?? "-";

  return (
    <Tooltip title={displayLabel} placement="top" arrow>
      <FormControl fullWidth>
        <Select
          displayEmpty
          disabled={isAutoResolved || isInFlight}
          labelId="action-select-label"
          id="action-simple-select"
          value={displayValue}
          onChange={(e) => {
            const actionToSet = Action[e.target.value as keyof typeof Action];
            if (isImmediateMode) {
              onDenialUpdate?.(row, {
                userAction: actionToSet ?? null,
                confirmAction: false,
              });
              return;
            }
            setPendingActionForClaim(row.wieldyId, actionToSet ?? null);
          }}
          sx={{
            ".MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
            borderRadius: "12px",
          }}
        >
          <MenuItem value="-">-</MenuItem>
          {Array.from(optionLabels.keys())
            .filter(
              (key) =>
                key !== Action.COMPLETED || displayValue === Action.COMPLETED,
            )
            .map((key) => (
              <MenuItem key={key} value={key}>
                {optionLabels.get(key)?.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </Tooltip>
  );
}

function renderDenialUrgency(
  props: ClaimCellParams,
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => void,
) {
  const { row } = props;
  if (row.claimStatus !== ClaimStatus.DENIED) return null;

  const urgencyName = (row.denialUrgency as string | null) ?? "-";
  const urgencyEnum = Urgency[urgencyName as keyof typeof Urgency];
  return (
    <FormControl fullWidth>
      <Select
        displayEmpty
        style={{
          color:
            typeof urgencyEnum === "number"
              ? urgencySelectColor.get(urgencyEnum)?.color
              : undefined,
          borderRadius: "12px",
        }}
        labelId="denial-urgency-select-label"
        id="denial-urgency-simple-select"
        value={urgencyName}
        onChange={(e) => {
          const selected = e.target.value;
          onDenialUpdate?.(row, {
            urgency: selected === "-" ? null : selected,
          });
        }}
        sx={{
          ".MuiOutlinedInput-notchedOutline": {
            border: 0,
          },
        }}
      >
        <MenuItem style={{ color: "#475467" }} value="-">
          -
        </MenuItem>
        {Array.from(urgencySelectColor.keys()).map((key) => (
          <MenuItem
            key={key}
            style={{ color: urgencySelectColor.get(key)?.color }}
            value={Urgency[key]}
          >
            {urgencySelectColor.get(key)?.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const TAKE_ACTION_LABELS: Partial<Record<Action, string>> = {
  [Action.RESUBMIT]: "Confirm Resubmitted",
  [Action.APPEAL]: "Confirm Appealed",
  [Action.SUBMIT_QUESTIONNAIRE]: "Confirm Submitted",
  [Action.SECONDARY_INSURANCE]: "Confirm Submitted",
  [Action.PATIENT_RESPONSIBILITY]: "Confirm Patient Responsibility",
  [Action.WRITE_OFF]: "Confirm Write-off",
  [Action.DUPLICATE]: "Confirm Duplicate",
  [Action.COMPLETED]: "Confirm Completed",
  [Action.CALL_PAYER]: "Set Denial Action",
};

function renderTakeActionButton(
  props: ClaimCellParams,
  onDenialUpdate:
    | ((
        claim: ClaimWithProcedureAndPatientMessage,
        denialWork: DenialWork,
      ) => void)
    | undefined,
  onOpenCallPayerDialog: (claim: ClaimWithProcedureAndPatientMessage) => void,
  inFlightDenialIds: Set<string>,
) {
  const { row } = props;
  if (row.claimStatus !== ClaimStatus.DENIED) return null;

  const isInFlight = inFlightDenialIds.has(row.wieldyId);

  const isAutoResolved = row.approvedClaimKey != null;
  const effectiveAction = (row.userAction ??
    row.suggestedAction ??
    null) as Action | null;
  const noAction =
    isAutoResolved ||
    effectiveAction === null ||
    effectiveAction === Action.NEEDS_REVIEW;

  const confirmedAt = row.actionConfirmedAt ?? null;
  const selectedAt = row.actionSelectedAt ?? null;
  const alreadyConfirmed =
    !noAction &&
    confirmedAt !== null &&
    (selectedAt === null || selectedAt <= confirmedAt);

  const isActionable = !noAction && !alreadyConfirmed;

  let label: string;
  if (noAction) {
    label = "Select action first";
  } else if (alreadyConfirmed) {
    label = "✓ Confirmed";
  } else {
    label = TAKE_ACTION_LABELS[effectiveAction as Action] ?? "Confirm Action";
  }

  return (
    <Button
      variant="contained"
      size="small"
      disabled={!isActionable || isInFlight}
      onClick={() => {
        if (effectiveAction === Action.CALL_PAYER) {
          onOpenCallPayerDialog(row);
          return;
        }
        onDenialUpdate?.(row, {
          userAction: effectiveAction,
          confirmAction: true,
        });
      }}
      sx={{
        textTransform: "none",
        fontSize: "0.72rem",
        lineHeight: 1.15,
        px: 1,
        py: 0,
        width: "75%",
        height: "36.5px",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
    >
      {isInFlight ? <CircularProgress size={16} /> : label}
    </Button>
  );
}

const DENIAL_STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  New: { bg: "#e8f5e9", color: "#2e7d32" },
  "Waiting on payer": { bg: "#fef3c7", color: "#92400e" },
  Resolved: { bg: "#fce4ec", color: "#b71c1c" },
};

function renderDenialStatus(status: string) {
  const colors = DENIAL_STATUS_COLORS[status] ?? {
    bg: "#f5f5f5",
    color: "#555",
  };
  return (
    <Box
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        borderRadius: "12px",
        px: 1.5,
        py: 0.5,
        fontSize: "0.8rem",
        fontWeight: 800,
        letterSpacing: "0.5px",
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      {status}
    </Box>
  );
}

function renderDoneButton(
  props: ClaimCellParams,
  pendingActionsByClaimId: Record<string, Action | null>,
  setPendingActionForClaim: (
    claimId: string,
    action: Action | null | undefined,
  ) => void,
  onDone:
    | ((
        claim: ClaimWithProcedureAndPatientMessage,
        claimWork: ClaimWork,
      ) => void)
    | undefined,
) {
  const { row } = props;

  if (row.claimStatus !== ClaimStatus.DENIED) return null;

  const hasPendingChange = row.wieldyId in pendingActionsByClaimId;
  const userAction = row.userAction ?? null;
  const suggestedAction = row.suggestedAction ?? null;
  const isAutoResolved = row.approvedClaimKey != null;

  let effectiveAction: Action | null = null;
  if (hasPendingChange) {
    effectiveAction = pendingActionsByClaimId[row.wieldyId];
  } else if (userAction === null && suggestedAction !== null) {
    effectiveAction = Action[suggestedAction as keyof typeof Action] ?? null;
  }

  const isDoneEnabled =
    !isAutoResolved &&
    effectiveAction !== Action.NEEDS_REVIEW &&
    (hasPendingChange || (userAction === null && suggestedAction !== null));

  return (
    <Button
      variant="contained"
      size="small"
      disabled={!isDoneEnabled}
      onClick={() => {
        const actionToSubmit = hasPendingChange
          ? pendingActionsByClaimId[row.wieldyId]
          : (Action[suggestedAction as keyof typeof Action] ?? null);
        const claimWork: ClaimWork = {
          wieldyId: row.wieldyId,
          practiceId: row.practiceId,
          action: actionToSubmit,
          userAction: actionToSubmit ?? null,
        };
        setPendingActionForClaim(row.wieldyId, undefined);
        onDone?.(row, claimWork);
      }}
    >
      Done
    </Button>
  );
}

const isColumnSortable = (field: string) => !!CLAIMS_SORT_FIELD_MAP[field];

const getColumns = (
  theme: Theme,
  enablePaymentNavigation: boolean,
  enumFilterOptions: Record<string, string[]>,
  onClaimUpdate: (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void,
  markManuallyPosted: (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    newIsPosted: boolean,
  ) => void,
  editClaim: ((claim: ClaimMessage) => void) | undefined,
  fetchScreenshots: (claim: ClaimMessage) => Promise<void>,
  resetClaim: (claimToUpdate: ClaimWithProcedureAndPatientMessage) => void,
  postToPMS: (claimToUpdate: ClaimWithProcedureAndPatientMessage) => void,
  searchParams: URLSearchParams,
  practiceValueOptions: string[],
  devMode: boolean,
  validateClaim?: (claim: ClaimWithProcedureAndPatientMessage) => void,
  deleteClaim?: (claim: ClaimWithProcedureAndPatientMessage) => void,
  unpostBlockedPracticeIds: string[] = [],
) => {
  const devModeOnlyColums: ClaimColDef[] = [
    {
      flex: 0.75,
      field: "claimType",
      headerName: "Claim Type",
    },
    {
      flex: 2,
      field: "bankMatchState",
      type: "singleSelect",
      headerName: "Bank Status",
      valueOptions: enumToStrings(BankMatchState).map((bankMatchState) => ({
        value: bankMatchState.toUpperCase(),
        label: snakeToTitle(bankMatchState),
      })),
    },
    {
      flex: 1,
      field: "postedByActor",
      headerName: "Posted By",
    },
    {
      flex: 1,
      field: "reviewStatus",
      type: "singleSelect",
      headerName: "Review Status",
      valueOptions: enumToStrings(ReviewStatus).map((reviewStatus) => ({
        value: reviewStatus.toUpperCase(),
        label: reviewStatus,
      })),
      renderCell: (params) => {
        const { reviewStatus, reviewReason } = params.row;
        if (
          validateClaim &&
          reviewReason &&
          reviewReason.length > 0 &&
          [ReviewStatus.MODIFIED, ReviewStatus.REVIEW].includes(
            reviewStatus.toUpperCase(),
          )
        ) {
          return (
            <Tooltip title={reviewReason || ""} arrow>
              <Button
                variant="contained"
                onClick={() => {
                  if (validateClaim) {
                    validateClaim(params.row);
                  }
                }}
              >
                {reviewStatus}
              </Button>
            </Tooltip>
          );
        }
        return <div>{reviewStatus}</div>;
      },
    },
    {
      flex: 1,
      field: "claimGroupName",
      headerName: "Claim Group Name",
    },
    {
      flex: 1,
      field: "claimGroupNumber",
      headerName: "Claim Group Number",
    },
    {
      flex: 1,
      field: "patientPayerNetwork",
      headerName: "Payer Network",
    },
    {
      flex: 1,
      field: "wieldyStatus",
      headerName: "Wieldy Status",
      type: "singleSelect",
      valueOptions: enumToStrings(WieldyStatus).map((wieldyStatus) => ({
        value: wieldyStatus.toUpperCase(),
        label: wieldyStatus,
      })),
    },
  ];

  const defaultColumns: ClaimColDef[] = [
    {
      flex: 1,
      field: "urgency",
      headerName: "Urgency",
      type: "singleSelect",
      valueOptions: enumToStrings(Urgency).map((urg) => ({
        value: urg.toUpperCase(),
        label: urg,
      })),
      valueGetter: (_value, row) => row.urgency ?? "-",
      renderCell: (props: ClaimCellParams) =>
        renderUrgency(props, onClaimUpdate),
      sortable: isColumnSortable("urgency"),
      filterOperators: CLAIM_SELECT_FILTER_OPERATORS,
    },
    {
      flex: 0.75,
      field: "practiceDisplayName",
      headerName: "Practice",
      type: "singleSelect",
      filterable: false,
      valueOptions: practiceValueOptions.length
        ? practiceValueOptions
        : undefined,
      sortable: isColumnSortable("practiceDisplayName"),
    },
    {
      flex: 1.25,
      field: "claimId",
      headerName: "Claim ID",
      renderCell: (params) => {
        const { wieldyPaymentId } = params.row;
        const claimID = String(params.formattedValue || params.value || "");
        const isValidPaymentNavigation =
          enablePaymentNavigation && wieldyPaymentId && claimID;

        if (isValidPaymentNavigation) {
          const { paymentId } = params.row;
          const linkPath = `/payments/${wieldyPaymentId}?${searchParams}`;
          const ariaLabel = paymentId
            ? `View Payment ID: ${paymentId}`
            : "View Payment ID";

          return (
            <NavigationCell
              linkPath={linkPath}
              displayText={claimID}
              ariaLabel={ariaLabel}
            />
          );
        }

        return renderCellRedacted(params);
      },
      sortable: isColumnSortable("claimId"),
    },
    {
      flex: 0.75,
      field: "payer",
      headerName: "Payer",
      type: "singleSelect",
      valueOptions: enumFilterOptions.payer || [],
      valueGetter: (_value, row) => normalizePayer(row.payer),
      renderCell: (params) => (params.value as string | null) ?? "-",
    },
    {
      flex: 0.75,
      field: "paymentId",
      headerName: "Payment ID",
      renderCell: (params: ClaimCellParams) =>
        params.row.wieldyPaymentId ? (
          <Link to={`/payments/${params.row.wieldyPaymentId}?${searchParams}`}>
            <Button
              data-sr-redact
              type="button"
              aria-label={`View Payment ID: ${params.row.paymentId}`}
              role="button"
              variant="text"
              style={{
                textDecoration: "underline",
                color: theme.palette.primary.main,
              }}
            >
              {params.row.paymentId}
            </Button>
          </Link>
        ) : (
          <div>-</div>
        ),
      sortable: isColumnSortable("paymentId"),
    },
    {
      flex: 1,
      field: "wieldyPatientName",
      headerName: "Patient Name",
      renderCell: (params: ClaimCellParams) => {
        const { wieldyPatientId } = params.row;
        const patientName =
          params.row.Patient?.length > 0
            ? params.row.Patient[0]?.name
            : params.value;
        if (!wieldyPatientId) {
          return renderCellRedacted(params);
        }
        return (
          <Link to={`/patients/${wieldyPatientId}?${searchParams}`}>
            <Button
              data-sr-redact
              type="button"
              aria-label={`View Patient ID: ${wieldyPatientId}`}
              role="button"
              variant="text"
              style={{
                textDecoration: "underline",
                color: theme.palette.primary.main,
              }}
            >
              {redactText(patientName as string | null)}
            </Button>
          </Link>
        );
      },
      sortable: isColumnSortable("wieldyPatientName"),
    },
    {
      flex: 0.7,
      type: "number",
      field: "claimPriorPaidAmount",
      headerName: "Adjustment Amount",
      valueFormatter: (value) => formatMoney(value as number | null),
      renderCell: renderCellRedacted,
      sortable: isColumnSortable("claimPriorPaidAmount"),
    },
    {
      flex: 1,
      type: "date",
      field: "patientDob",
      headerName: "Patient DOB",
      valueGetter: (_value, row) =>
        row.patientDob ? isoToDate(row.patientDob) : null,
      renderCell: renderCellRedacted,
      sortable: isColumnSortable("patientDob"),
    },
    {
      flex: 1,
      field: "wieldySubscriberName",
      headerName: "Subscriber Name",
      sortable: isColumnSortable("wieldySubscriberName"),
    },
    {
      flex: 1,
      field: "subscriberId",
      headerName: "Subscriber ID",
      sortable: isColumnSortable("subscriberId"),
    },
    {
      flex: 1,
      field: "relationshipToSubscriber",
      headerName: "Relationship To Subscriber",
      sortable: isColumnSortable("relationshipToSubscriber"),
    },
    {
      flex: 1,
      field: "claimStatus",
      headerName: "Claim Status",
      type: "singleSelect",
      valueOptions: [{ value: "-1", label: "-" }].concat(
        enumToStrings(ClaimStatus).map((status) => ({
          value: status,
          label: toPascalCase(status).replace("_", " "),
        })),
      ),
      renderCell: (params: ClaimCellParams) =>
        params.row.claimStatus ? toPascalCase(params.row.claimStatus) : "-",
      valueGetter: (_value, row) => row.claimStatus ?? "-1",
      sortable: isColumnSortable("claimStatus"),
    },
    {
      flex: 0.7,
      type: "number",
      field: "claimPayerPays",
      headerName: "Payer Pays",
      valueFormatter: (value) => formatMoney(value as number | null),
      renderCell: renderCellRedacted,
      sortable: isColumnSortable("claimPayerPays"),
    },
    {
      flex: 0.6,
      field: "documents",
      headerName: "PDF",
      disableExport: true,
      renderCell: (props: ClaimCellParams) => {
        const sourceUrl = head(
          toSourceUrls(props.value as ClaimRow["documents"]),
        );
        if (!sourceUrl) {
          return null;
        }
        return (
          <DownloadSource
            url={sourceUrl}
            downloadFileName={toPDFFileName(
              props.row.payer,
              props.row.paymentDate,
            )}
          />
        );
      },
      sortable: isColumnSortable("documents"),
    },
    {
      flex: 1,
      type: "date",
      field: "availableSince",
      headerName: "Available Since",
      valueGetter: (_value, row) =>
        row.availableSince ? isoToDate(row.availableSince) : null,
      sortable: isColumnSortable("availableSince"),
    },
    {
      flex: 1,
      type: "date",
      field: "wieldyClaimDate",
      headerName: "Claim Date",
      valueGetter: (_value, row) =>
        row.wieldyClaimDate ? isoToDate(row.wieldyClaimDate) : null,
      sortable: isColumnSortable("wieldyClaimDate"),
    },
    {
      flex: 1,
      field: "dateOfService",
      headerName: "Date of Service",
      valueGetter: (value) => value || "",
      sortable: isColumnSortable("dateOfService"),
    },
    {
      flex: 1,
      type: "singleSelect",
      field: "action",
      headerName: "Action",
      valueOptions: enumToStrings(Action).map((action) => ({
        value: action.toString(),
        label: toPascalCase(action).replace("_", " "),
      })),
      renderCell: (props: ClaimCellParams) =>
        renderAction(props, onClaimUpdate),
      valueGetter: (_value, row) => row.action ?? "-",
      sortable: isColumnSortable("action"),
      filterOperators: CLAIM_SELECT_FILTER_OPERATORS,
    },
    {
      flex: 2,
      field: "postedState",
      type: "singleSelect",
      headerName: "Posted",
      valueOptions: enumToStrings(PostedState).map((postedState) => ({
        value: postedState.toUpperCase(),
        label: postedState,
      })),
      renderCell: (props: ClaimCellParams) =>
        postButton(props, markManuallyPosted, postToPMS, searchParams),
      sortable: isColumnSortable("postedState"),
    },
    {
      flex: 1.5,
      type: "date",
      field: "postedDateTime",
      headerName: "Posted Date",
      renderCell: (params: ClaimCellParams) =>
        params.row.postedDateTime
          ? `${new Date(params.row.postedDateTime).toLocaleDateString()} ${new Date(
              params.row.postedDateTime,
            ).toLocaleTimeString(navigator.language, {
              hour: "2-digit",
              minute: "2-digit",
            })}`
          : "-",
      valueGetter: (_value, row) =>
        row.postedDateTime ? new Date(row.postedDateTime) : null,
      sortable: isColumnSortable("postedDateTime"),
    },
    {
      flex: 1,
      field: "notes",
      headerName: "Notes*",
      renderCell: (params) => {
        const notes = params.row?.notes || "-";
        return <div data-sr-redact>{notes}</div>;
      },
      sortable: isColumnSortable("notes"),
    },
  ];

  if (devMode) {
    defaultColumns.push(...devModeOnlyColums);
    defaultColumns.push({
      flex: 1,
      field: "screenshots",
      headerName: "Screenshots",
      align: "center",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: ClaimCellParams) => {
        const claim = params.row;
        if (claim.postedState === PostedState[PostedState.AUTO_POSTED]) {
          return (
            <CheckCircleIcon
              color="success"
              onClick={async () => {
                await fetchScreenshots(claim);
              }}
            >
              <EditIcon />
            </CheckCircleIcon>
          );
        }
        if (hasFailed(claim)) {
          return (
            <ErrorOutlineIcon
              color="error"
              onClick={async () => {
                await fetchScreenshots(claim);
              }}
            >
              <EditIcon />
            </ErrorOutlineIcon>
          );
        }
        if (claim.postedState === PostedState[PostedState.MANUALLY_POSTED]) {
          return (
            <ErrorOutlineIcon
              color="warning"
              onClick={async () => {
                await fetchScreenshots(claim);
              }}
            >
              <EditIcon />
            </ErrorOutlineIcon>
          );
        }
        return "-";
      },
    });
    defaultColumns.push({
      flex: 1.5,
      field: "edit",
      headerName: "Edit",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: ClaimCellParams) => (
        <>
          {editClaim ? (
            <IconButton
              onClick={() => {
                editClaim(params.row);
              }}
            >
              <EditIcon />
            </IconButton>
          ) : null}
          {deleteClaim ? (
            <IconButton
              onClick={() => {
                deleteClaim(params.row);
              }}
            >
              <DeleteIcon />
            </IconButton>
          ) : null}
        </>
      ),
    });
    defaultColumns.push({
      flex: 2,
      field: "resetClaim",
      headerName: "Reset Claim",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params: ClaimCellParams) => {
        const blocked = unpostBlocked(params.row, unpostBlockedPracticeIds);
        return (
          <Tooltip
            title={
              blocked
                ? "Likely already posted. Contact the team for support."
                : ""
            }
            placement="top"
            arrow
          >
            <span style={{ width: "100%" }}>
              <Button
                data-testid="unpostClaim"
                disabled={
                  blocked ||
                  params.row.postedState === PostedState[PostedState.UNPOSTED]
                }
                type="button"
                variant="contained"
                style={{
                  cursor: "pointer",
                  width: "100%",
                }}
                onClick={() => {
                  resetClaim(params.row);
                }}
              >
                Unpost Claim
              </Button>
            </span>
          </Tooltip>
        );
      },
    });
  }

  return defaultColumns;
};

export interface ClaimsTablePagination {
  page: number;
  pageSize: number;
  rowCount: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
}

interface ClaimsTableProps {
  claims: ClaimWithProcedureAndPatientMessage[];
  isLoading: boolean;
  onClaimUpdate: (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onPostToPMS: (claimToUpdate: ClaimWithProcedureAndPatientMessage) => void;
  onSaveClaim?: (claim: ClaimMessage, file: File | null) => Promise<void>;
  allowAddClaim?: boolean;
  onDeleteClaim?: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  onSaveProcedure?: (procedure: ProcedureMessage) => Promise<void>;
  onDeleteProcedure?: (procedure: ProcedureMessage) => Promise<void>;
  enableClaimDetails?: boolean;
  enablePaymentNavigation?: boolean;
  initialSortingModel?: SortModel;
  excludeByFieldNames?: string[];
  sortColumnOrder?: string[];
  practices: PracticeMessage[];
  supportedPayers?: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  payment: PaymentMessage | null;
  persistKey?: string;
  onFilterChange?: (filters: BackendFilters) => void;
  pagination?: ClaimsTablePagination;
  onSortChange?: (
    sortField: string | null,
    sortDirection: "asc" | "desc",
  ) => void;
  currentFilters?: BackendFilters;
  currentTab?: string | null;
  activeView?: REVIEW_VIEW | null;
  updateSnackBar?: (snackbar: {
    severity: AlertColor;
    message: string;
  }) => void;
  validateClaim?: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  onDone?: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => Promise<void>;
  hideToolbar?: boolean;
  hideColumnHeaders?: boolean;
  columnVisibilityModel?: ColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: ColumnVisibilityModel) => void;
  columnWidths?: Record<string, number>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
}

export default function ClaimsTable({
  claims,
  isLoading,
  onClaimUpdate,
  onPostToPMS,
  onSaveClaim,
  allowAddClaim = true,
  onDeleteClaim,
  onSaveProcedure,
  onDeleteProcedure,
  enableClaimDetails = false,
  enablePaymentNavigation = false,
  initialSortingModel = [
    { field: "urgency", sort: "desc" },
    { field: "wieldyClaimDate", sort: "desc" },
  ],
  excludeByFieldNames = [],
  sortColumnOrder = DEFAULT_COLUMN_ORDER,
  practices,
  supportedPayers = [],
  supportedSources,
  payment,
  persistKey,
  onFilterChange = () => undefined,
  pagination,
  onSortChange = () => undefined,
  currentFilters = createDefaultBackendFilters(),
  currentTab = null,
  activeView = null,
  updateSnackBar,
  validateClaim,
  onDone,
  onDenialUpdate,
  hideToolbar = false,
  hideColumnHeaders = false,
  columnVisibilityModel: controlledColumnVisibilityModel,
  onColumnVisibilityModelChange,
  columnWidths,
  onColumnWidthsChange,
}: ClaimsTableProps) {
  const isColumnVisibilityControlled =
    controlledColumnVisibilityModel !== undefined;
  const location = useLocation();
  const muiTheme = useTheme();
  const searchParams = new URLSearchParams(location.search);
  const [openClaimModal, setOpenClaimModal] = React.useState(false);
  const [editingClaimPosted, setEditingClaimPosted] = React.useState(false);
  const [claimToUnpost, setClaimToUnpost] =
    useState<ClaimWithProcedureAndPatientMessage | null>(null);
  const unpostPartial = claimToUnpost ? partialPosting(claimToUnpost) : null;
  const [claimsToDelete, setClaimsToDelete] =
    React.useState<ClaimWithProcedureAndPatientMessage | null>(null);
  const [claimFileToUpload, setClaimFileToUpload] = React.useState<File | null>(
    null,
  );
  const [fileError, setFileError] = useState<string | null>(null);
  const [screenshotsModalOpen, setScreenshotsModalOpen] = useState(false);
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [screenshotsClaim, setScreenshotsClaim] = useState<ClaimMessage | null>(
    null,
  );
  const [loadingScreenshots, setLoadingScreenshots] = useState(false);
  const queryClient = useQueryClient();
  const [enumFilterOptions, setEnumFilterOptions] = useState<
    Record<string, string[]>
  >({});
  const [isExporting, setIsExporting] = useState(false);

  const devMode = useDevMode();
  const {
    config: { unpostBlockedPracticeIds },
  } = useConfig();
  const { isEnabled: isDeniseEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.DENISE,
  );

  const [pendingActionsByClaimId, setPendingActionsByClaimId] = useState<
    Record<string, Action | null>
  >({});
  const [callPayerClaim, setCallPayerClaim] =
    useState<ClaimWithProcedureAndPatientMessage | null>(null);
  const [inFlightDenialIds, setInFlightDenialIds] = useState<Set<string>>(
    new Set(),
  );

  const trackedDenialUpdate = React.useMemo(() => {
    if (onDenialUpdate === undefined) return undefined;
    return async (
      claim: ClaimWithProcedureAndPatientMessage,
      denialWork: DenialWork,
    ) => {
      setInFlightDenialIds((prev) => new Set(prev).add(claim.wieldyId));
      try {
        await onDenialUpdate(claim, denialWork);
      } finally {
        setInFlightDenialIds((prev) => {
          const next = new Set(prev);
          next.delete(claim.wieldyId);
          return next;
        });
      }
    };
  }, [onDenialUpdate]);

  const setPendingActionForClaim = useCallback(
    (claimId: string, action: Action | null | undefined) => {
      setPendingActionsByClaimId((prev) => {
        if (action === undefined) {
          const next = { ...prev };
          delete next[claimId];
          return next;
        }
        return { ...prev, [claimId]: action };
      });
    },
    [],
  );

  const {
    control: claimControl,
    formState: { errors, isSubmitting, isValid },
    handleSubmit: handleSubmitClaim,
    reset,
  } = useForm<AddClaimFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(claimFormSchemaBase),
    defaultValues: defaultClaimFields,
  });

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey,
    mapField: mapClaimFilterField,
    onFiltersChange: onFilterChange,
  });

  const [sortModel, setSortModel] = useState<SortModel>(initialSortingModel);

  const handleSortModelChange = (model: SortModel) => {
    setSortModel(model);
    if (model.length > 0) {
      const { field, sort } = model[0];
      onSortChange(mapClaimSortField(field), sort);
    } else {
      onSortChange(null, "desc");
    }
  };

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const toggleExpanded = useCallback(
    (id: string) =>
      setExpandedIds((current) =>
        current.includes(id)
          ? current.filter((openId) => openId !== id)
          : [...current, id],
      ),
    [],
  );

  const [clientPage, setClientPage] = useState<PaginationModel>({
    page: 0,
    pageSize: 10,
  });

  const normalizeEnumFilterOptions = (options: string[]) => {
    const seen = new Set();
    return (options || [])
      .map((opt) => normalizePayer(opt))
      .filter((opt) => {
        if (seen.has(opt)) return false;
        seen.add(opt);
        return true;
      });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding normalizeEnumFilterOptions, queryClient.getClaimEnumFilters would change when this runs
  useEffect(() => {
    const fetchEnumOptions = async () => {
      try {
        const options = await queryClient.getClaimEnumFilters(["payer"], {});
        setEnumFilterOptions(
          Object.fromEntries(
            Object.entries(options).map(([key, value]) => [
              key,
              normalizeEnumFilterOptions(value).sort(sortAlphabetically),
            ]),
          ),
        );
      } catch {
        setEnumFilterOptions({});
      }
    };
    fetchEnumOptions();
  }, []);

  const fetchScreenshots = async (claim: ClaimMessage) => {
    setLoadingScreenshots(true);
    setScreenshotsClaim(claim);
    try {
      const fetchedShots = await queryClient.getScreenshots(claim.wieldyId);
      setScreenshots(fetchedShots);
      setScreenshotsModalOpen(true);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(error);
    } finally {
      setLoadingScreenshots(false);
    }
  };

  const onEditClaim = (claim: ClaimMessage) => {
    const formFields = editableClaimFields.reduce<AddClaimFormFields>(
      (acc, field) =>
        Object.assign(acc, {
          [field.name]:
            // We pass payer from FE to BE in titlecase but the BE
            // expects all uppercase.
            field.name === "payer"
              ? claim[field.name as keyof typeof claim]?.toUpperCase()
              : (claim[field.name as keyof typeof claim] ??
                defaultClaimFields[
                  field.name as keyof typeof defaultClaimFields
                ]),
        }),
      // We only have wieldyId when editing fields so make sure
      // it exists or we'll accidentally CREATE a claim instead
      // since CREATE claim don't have wieldyId and EDITs
      // do
      { wieldyId: claim.wieldyId } as AddClaimFormFields,
    );
    reset(formFields);
    setEditingClaimPosted(hasPosted(claim));
    setOpenClaimModal(true);
  };

  const onClaimModal = (open: boolean) => {
    if (open) {
      setEditingClaimPosted(false);
      reset({
        ...defaultClaimFields,
        paymentKey: payment?.wieldyId || "",
        paymentId: payment?.payerPaymentId || "",
        practiceId: payment?.practiceId || "",
        source: payment?.source || "",
      });
    }
    setOpenClaimModal(open);
  };

  const onOpenDeleteModal = (claim: ClaimWithProcedureAndPatientMessage) => {
    setClaimsToDelete(claim);
  };

  const onCloseDeleteModal = () => {
    setClaimsToDelete(null);
  };

  const onConfirmDeleteClaim = async () => {
    if (claimsToDelete && onDeleteClaim) {
      await onDeleteClaim(claimsToDelete);
    }
    setClaimsToDelete(null);
  };

  const onClaimFormSubmission = handleSubmitClaim(async (data) => {
    if (onSaveClaim) {
      await onSaveClaim(data as ClaimMessage, claimFileToUpload);
    }
  });

  const claimHasErrors = Object.keys(errors).length > 0;
  const claimIsDisabled = isSubmitting || claimHasErrors || !isValid;

  const markManuallyPosted = (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
    newIsPosted: boolean,
  ) => {
    onClaimUpdate(claimToUpdate, {
      wieldyId: claimToUpdate.wieldyId,
      practiceId: claimToUpdate.practiceId,
      postedDateTime: newIsPosted ? isoNow() : null,
      postedState: newIsPosted
        ? PostedState[PostedState.MANUALLY_POSTED]
        : PostedState[PostedState.UNPOSTED],
    });
  };

  const resetClaim = async (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
  ) => {
    try {
      await queryClient.resetClaim(claimToUpdate.wieldyId);
      onClaimUpdate(claimToUpdate, {
        wieldyId: claimToUpdate.wieldyId,
        practiceId: claimToUpdate.practiceId,
        postedDateTime: null,
        postedState: PostedState[PostedState.UNPOSTED],
        postedAttempts: 0,
        postedLatestErrorMessage: null,
        postingCompletedSteps: [],
        postingExpectedSteps: [],
      });
    } catch (error) {
      updateSnackBar?.({
        severity: "error",
        message:
          (error instanceof AxiosError &&
            typeof error.response?.data?.detail === "string" &&
            error.response.data.detail) ||
          "Could not unpost the claim.",
      });
    }
  };

  const requestResetClaim = (
    claimToUpdate: ClaimWithProcedureAndPatientMessage,
  ) => {
    setClaimToUnpost(claimToUpdate);
  };

  const onConfirmUnpostClaim = async () => {
    if (claimToUnpost) {
      await resetClaim(claimToUnpost);
    }
    setClaimToUnpost(null);
  };

  const renderModalContent = () => {
    if (loadingScreenshots) {
      return <CircularProgress />;
    }

    if (!screenshots.length) {
      return "No screenshots found.";
    }

    const isManuallyPosted =
      screenshotsClaim?.postedState ===
      PostedState[PostedState.MANUALLY_POSTED];

    return (
      <>
        {isManuallyPosted && (
          <Typography variant="subtitle2" color="warning.main" sx={{ mb: 2 }}>
            Showing screenshot from last posting attempt before manual posting
          </Typography>
        )}
        {screenshots.map((screenshot, index) => (
          <img
            key={screenshot}
            src={screenshot}
            alt={`Screenshot ${index + 1}`}
            style={{ maxWidth: "100%", marginBottom: "1rem" }}
          />
        ))}
      </>
    );
  };

  const renderClaimDetails = (row: ClaimRow) => (
    <ClaimDetails
      claim={row}
      onClaimDetailUpdate={onClaimUpdate}
      practices={practices}
      supportedSources={supportedSources}
      onSaveProcedure={onSaveProcedure}
      onDeleteProcedure={onDeleteProcedure}
    />
  );

  const columnVisibility = (() => {
    const base = (() => {
      if (currentTab === PREDEFINED_CLAIM_FILTERS.REVIEW) {
        return getReviewColumnVisibility(isDeniseEnabled, activeView);
      }
      if (currentTab === PREDEFINED_CLAIM_FILTERS.DENIALS) {
        return getDenialsColumnVisibility(isDeniseEnabled);
      }
      if (currentTab === PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS) {
        return getAllClaimsColumnVisibility(isDeniseEnabled);
      }
      return getDefaultColumnVisibility();
    })();
    return (excludeByFieldNames || []).reduce(
      (visibilityModel, current) =>
        visibilityModel[current]
          ? // biome-ignore lint/performance/noAccumulatingSpread: base may be a shared default, so it must not be mutated
            { ...visibilityModel, [current]: false }
          : visibilityModel,
      base,
    );
  })();

  // Saved per-tab choices win, except on Review and with DENISE on.
  // biome-ignore lint/correctness/useExhaustiveDependencies: recompute only when the tab or view changes, as the old grid did
  const tabVisibility = React.useMemo(() => {
    if (isColumnVisibilityControlled) return undefined;
    const usesSaved =
      persistKey &&
      currentTab &&
      currentTab !== PREDEFINED_CLAIM_FILTERS.REVIEW &&
      !isDeniseEnabled;
    return (
      (usesSaved && loadTabVisibility(persistKey, currentTab)) ||
      columnVisibility
    );
  }, [currentTab, isDeniseEnabled, activeView]);

  const handleColumnVisibilityChange = useCallback(
    (model: ColumnVisibilityModel) => {
      onColumnVisibilityModelChange?.(model);
      if (currentTab && persistKey) {
        saveTabVisibility(persistKey, currentTab, model);
      }
    },
    [currentTab, persistKey, onColumnVisibilityModelChange],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding onSaveClaim, fetchScreenshots, devMode and 14 more would change when this runs
  const columns = React.useMemo(() => {
    const cols = getColumns(
      muiTheme,
      !!enablePaymentNavigation,
      enumFilterOptions,
      onClaimUpdate,
      markManuallyPosted,
      onSaveClaim && !hideToolbar ? onEditClaim : undefined,
      fetchScreenshots,
      requestResetClaim,
      onPostToPMS,
      searchParams,
      toValueOptions(claims, "practiceDisplayName", sortAlphabetically),
      devMode,
      validateClaim,
      onDeleteClaim ? onOpenDeleteModal : undefined,
      unpostBlockedPracticeIds,
    );

    if (isDeniseEnabled) {
      const colsWithDeniseAction = cols.map((col) => {
        if (col.field === "urgency") {
          return { ...col, filterable: false };
        }
        if (col.field !== "action") return col;
        return {
          ...col,
          headerName: "Denial action",
          renderCell: (props: ClaimCellParams) =>
            renderActionDenise(
              props,
              pendingActionsByClaimId,
              setPendingActionForClaim,
              inFlightDenialIds,
              trackedDenialUpdate,
            ),
        };
      });

      const deniseColumns: ClaimColDef[] = [
        {
          flex: 1,
          field: "denialStatus",
          headerName: "Denial Status",
          sortable: false,
          type: "singleSelect",
          valueOptions: [
            { value: DenialStatus.NEW, label: "New" },
            { value: DenialStatus.WAITING_ON_PAYER, label: "Waiting on payer" },
            { value: DenialStatus.RESOLVED, label: "Resolved" },
          ],
          renderCell: (params: ClaimCellParams) => {
            if (params.row.claimStatus !== ClaimStatus.DENIED) return null;
            const status = params.value as string | undefined;
            if (!status) return <div>-</div>;
            return renderDenialStatus(status);
          },
        },
        {
          flex: 0.7,
          field: "doneButton",
          headerName: "Done",
          sortable: false,
          filterable: false,
          disableExport: true,
          renderCell: (params: ClaimCellParams) =>
            renderDoneButton(
              params,
              pendingActionsByClaimId,
              setPendingActionForClaim,
              onDone,
            ),
        },
        {
          flex: 1,
          field: "daysSinceNotice",
          headerName: "Days Since Notice",
          sortable: true,
          filterable: false,
          renderCell: (params: ClaimCellParams) => (
            <div>
              {(params.row.daysSinceNotice as number | undefined) ?? "-"}
            </div>
          ),
        },
        {
          flex: 1,
          field: "daysSinceService",
          headerName: "Days Since Service",
          sortable: true,
          filterable: false,
          renderCell: (params: ClaimCellParams) => (
            <div>
              {(params.row.daysSinceService as number | undefined) ?? "-"}
            </div>
          ),
        },
        {
          flex: 0.9,
          field: "denialUrgency",
          headerName: "Denial Urgency",
          type: "singleSelect",
          valueOptions: enumToStrings(Urgency).map((urg) => ({
            value: urg.toUpperCase(),
            label: urg,
          })),
          sortable: false,
          filterable: true,
          filterOperators: CLAIM_SELECT_FILTER_OPERATORS,
          renderCell: (params: ClaimCellParams) =>
            renderDenialUrgency(params, trackedDenialUpdate),
        },
        {
          flex: 1,
          field: "takeActionButton",
          headerName: "Take Action",
          sortable: false,
          filterable: false,
          disableExport: true,
          renderCell: (params: ClaimCellParams) =>
            renderTakeActionButton(
              params,
              trackedDenialUpdate,
              setCallPayerClaim,
              inFlightDenialIds,
            ),
        },
      ];

      return sortColumnsByFieldOrder({
        gridColumns: [...colsWithDeniseAction, ...deniseColumns],
        gridColFieldOrder: sortColumnOrder,
      });
    }

    return sortColumnsByFieldOrder({
      gridColumns: cols,
      gridColFieldOrder: sortColumnOrder,
    });
  }, [
    muiTheme,
    claims,
    isDeniseEnabled,
    trackedDenialUpdate,
    inFlightDenialIds,
    pendingActionsByClaimId,
    setPendingActionForClaim,
    unpostBlockedPracticeIds,
  ]);

  const tableColumns = React.useMemo<ClaimColDef[]>(
    () =>
      enableClaimDetails
        ? [
            {
              field: DETAIL_TOGGLE_FIELD,
              headerName: "",
              width: 56,
              align: "center",
              sortable: false,
              filterable: false,
              hideable: false,
              disableColumnMenu: true,
              disableExport: true,
              renderCell: ({ row }) => {
                const open = expandedIds.includes(row.wieldyId);
                return (
                  <IconButton
                    size="small"
                    aria-label={open ? "Collapse" : "Expand"}
                    aria-expanded={open}
                    onClick={() => toggleExpanded(row.wieldyId)}
                  >
                    {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                );
              },
            },
            ...columns,
          ]
        : columns,
    [enableClaimDetails, columns, expandedIds, toggleExpanded],
  );

  const handleExportAllClaims = async (exportColumns: ClaimColDef[]) => {
    if (isExporting) return;
    const visibleColumns = exportColumns.map((col) => col.field);
    try {
      setIsExporting(true);

      const mappedTab = currentTab
        ? mapFilterToTab(currentTab as PREDEFINED_CLAIM_FILTERS)
        : null;

      const exportResponse =
        await queryClient.getClaimsProceduresAndPatientForExport(
          [],
          undefined, // No abort signal for export
          currentFilters,
          mappedTab,
          null, // Use default sort
          "desc",
        );

      if (exportResponse.data.length === 0) {
        updateSnackBar?.({
          severity: "error",
          message: "No claims to export with current filters",
        });
        return;
      }

      const exportableColumns = columns.filter(
        (col) =>
          col.field !== "actions" &&
          col.type !== "actions" &&
          !col.field.startsWith("__") &&
          !col.disableExport,
      );

      const columnFieldMap = new Map(
        exportableColumns.map((col) => [
          col.field,
          col.headerName || col.field,
        ]),
      );

      // Fields included in the CSV but never shown in the grid.
      const exportOnlyHeaderByField = new Map<string, string>([
        ["pmsPatientId", "PMS Patient ID"],
      ]);

      const columnsToExport = visibleColumns.filter((field) =>
        columnFieldMap.has(field),
      );

      const patientNameIdx = columnsToExport.indexOf("wieldyPatientName");
      const insertAt =
        patientNameIdx >= 0 ? patientNameIdx + 1 : columnsToExport.length;
      columnsToExport.splice(insertAt, 0, "pmsPatientId");

      const headers = columnsToExport.map(
        (field) =>
          columnFieldMap.get(field) ||
          exportOnlyHeaderByField.get(field) ||
          field,
      );

      const csvContent = [
        headers.join(","),
        ...exportResponse.data.map(
          (claim: ClaimWithProcedureAndPatientMessage) =>
            columnsToExport
              .map((field) => {
                let value =
                  claim[field as keyof ClaimWithProcedureAndPatientMessage];

                if (field === "payer" && claim.payer) {
                  value = claim.payer;
                } else if (field === "patientDob" && claim.patientDob) {
                  value = new Date(claim.patientDob).toLocaleDateString();
                } else if (field === "claimStatus" && claim.claimStatus) {
                  value = claim.claimStatus.replace(/_/g, " ");
                } else if (field === "wieldyPatientName") {
                  value =
                    claim.Patient?.length > 0
                      ? claim.Patient[0]?.name
                      : claim.wieldyPatientName;
                }

                return value != null ? String(value) : "";
              })
              .map((field) => `"${String(field).replace(/"/g, '""')}"`)
              .join(","),
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `claims-export-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const isServerPagination = !!pagination;
  const clientLastPage = Math.max(
    0,
    Math.ceil(claims.length / clientPage.pageSize) - 1,
  );
  const clientPageModel = {
    page: Math.min(clientPage.page, clientLastPage),
    pageSize: clientPage.pageSize,
  };
  const pageModel = isServerPagination
    ? { page: pagination.page - 1, pageSize: pagination.pageSize }
    : clientPageModel;
  const pageRows = isServerPagination
    ? claims
    : claims.slice(
        clientPageModel.page * clientPageModel.pageSize,
        (clientPageModel.page + 1) * clientPageModel.pageSize,
      );

  const onPaginationModelChange = (next: PaginationModel) => {
    if (!isServerPagination) {
      setClientPage(next);
      return;
    }
    if (next.page !== pageModel.page) pagination.onPageChange(next.page + 1);
    if (next.pageSize !== pageModel.pageSize)
      pagination.onPageSizeChange(next.pageSize);
  };

  return (
    <>
      <Dialog
        open={!!claimsToDelete}
        onClose={onCloseDeleteModal}
        sx={{
          "& .MuiPaper-root": {
            width: "100%",
            padding: "24px 36px",
            margin: "auto",
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ marginBottom: 2, p: 0 }} id="delete-claim-title">
          Delete Claim
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onCloseDeleteModal}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: 0, margin: 0 }}>
          <Typography
            paragraph
            sx={{
              fontSize: "0.875rem",
              fontWeight: 400,
              color: "#475467",
              marginBottom: 0,
            }}
          >
            Are you sure you want to delete the claim?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            sx={{ borderRadius: "8px" }}
            color="error"
            variant="contained"
            onClick={onConfirmDeleteClaim}
            data-testid="submitCredentials"
          >
            Delete Claim
          </Button>
        </DialogActions>
      </Dialog>
      <UnpostDialog
        open={!!claimToUnpost}
        title="Unpost claim"
        description="Before unposting, open this patient's ledger in the PMS and confirm the payment is not there. Unposting clears what Wieldy recorded, and the next post enters the payment again."
        details={
          unpostPartial ? (
            <Typography
              data-testid="unpostLandedSummary"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#475467",
                marginTop: 1.5,
              }}
            >
              Wieldy recorded:{" "}
              {[...unpostPartial.landed, ...unpostPartial.missing].join(" · ")}
            </Typography>
          ) : null
        }
        confirmTestId="confirmUnpostClaim"
        onClose={() => setClaimToUnpost(null)}
        onConfirm={onConfirmUnpostClaim}
      />
      <div style={{ width: "100%" }}>
        <DataTable
          loading={isLoading}
          persistKey={persistKey}
          rows={pageRows}
          columns={tableColumns}
          getRowId={(row) => row.wieldyId}
          rowCount={isServerPagination ? pagination.rowCount : claims.length}
          paginationModel={pageModel}
          pageSizeOptions={[...new Set([pageModel.pageSize, 10, 20, 40])].sort(
            (a, b) => a - b,
          )}
          onPaginationModelChange={onPaginationModelChange}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityOverrides={tabVisibility}
          columnVisibilityModel={controlledColumnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          columnWidths={columnWidths}
          onColumnWidthsChange={onColumnWidthsChange}
          hideHeader={hideColumnHeaders}
          showToolbar={!hideToolbar}
          onExportCsv={handleExportAllClaims}
          exporting={isExporting}
          expandedRowIds={expandedIds}
          renderDetailPanel={
            enableClaimDetails ? renderClaimDetails : undefined
          }
          noRowsOverlay={<CustomNoRowsOverlay />}
          toolbarActions={
            onSaveClaim ? (
              <AddEntityActions
                addEntityForm={
                  <ClaimForm
                    payment={payment}
                    control={claimControl}
                    practices={practices}
                    supportedPayers={supportedPayers || []}
                    supportedSources={supportedSources || []}
                    setClaimFileToUpload={setClaimFileToUpload}
                    claimFileToUpload={claimFileToUpload}
                    setFileError={setFileError}
                    fileError={fileError}
                    isPosted={editingClaimPosted}
                  />
                }
                handleSubmit={onClaimFormSubmission}
                isDisabled={claimIsDisabled}
                entityName="Claim"
                enableAddToolBar
                enableAddRow={allowAddClaim}
                setOpenModal={onClaimModal}
                openModal={openClaimModal}
              />
            ) : undefined
          }
        />
        <SetNextActionDialog
          claim={callPayerClaim}
          onClose={() => setCallPayerClaim(null)}
          onSave={async (nextAction) => {
            if (!callPayerClaim) return;
            await onDenialUpdate?.(callPayerClaim, {
              userAction: Action.CALL_PAYER,
              confirmAction: true,
            });
            await onDenialUpdate?.(callPayerClaim, {
              userAction: nextAction,
              confirmAction: false,
            });
            setCallPayerClaim(null);
          }}
        />
        <Modal
          open={screenshotsModalOpen}
          onClose={() => {
            setScreenshotsModalOpen(false);
            setScreenshotsClaim(null);
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              padding: 4,
              width: "70%",
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              borderRadius: 3,
            }}
          >
            {renderModalContent()}
          </Box>
        </Modal>
      </div>
    </>
  );
}
